import styled, { css } from "styled-components";

import { DateParts, cancelEvent, getDateParts } from "../../utils";

type Props = {
  day: Date;
  currentMonth: number;
  onClick: (day: Date) => void;
  onMouseOver: (day: Date) => void;
  startParts: DateParts;
  endParts: DateParts;
};

export function Day({
  day,
  currentMonth,
  onClick,
  onMouseOver,
  startParts,
  endParts,
}: Props) {
  const dayParts = getDateParts(day);

  const isStartDay =
    dayParts.month === startParts.month &&
    dayParts.date === startParts.date &&
    dayParts.year === startParts.year;

  const isEndDay =
    dayParts.month === endParts.month &&
    dayParts.date === endParts.date &&
    dayParts.year === endParts.year;

  // You can only be between if you're not already start/end
  const isBetween =
    !isStartDay &&
    !isEndDay &&
    dayParts.timestamp > startParts.timestamp &&
    dayParts.timestamp < endParts.timestamp;

  const className = `${isStartDay || isEndDay ? "selected" : ""} ${
    isBetween ? "semi-selected" : ""
  }`;

  return (
    <DateItem
      tabIndex={-1}
      outside={day.getMonth() !== currentMonth}
      className={className}
      onClick={(event) => {
        cancelEvent(event);
        onClick(day);
      }}
      onMouseOver={() => onMouseOver(day)}
      isStartDay={isStartDay}
      isEndDay={isEndDay}
    >
      <StyledDay>
        {day.getDate().toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })}
      </StyledDay>
    </DateItem>
  );
}

const DateItem = styled.td<{
  outside: boolean;
  isStartDay: boolean;
  isEndDay: boolean;
}>(
  ({ theme, outside, isStartDay, isEndDay }) => css`
    text-align: center;
    cursor: pointer;
    opacity: ${outside ? "0.5" : "1.0"};

    &.selected ${StyledDay} {
      background-color: ${theme.color.bg.emphasis.primary};
      color: ${theme.color.fg.onemphasis.default};
    }

    &.selected + .semi-selected {
      position: relative;

      &::before {
        position: absolute;
        content: "";
        background: ${theme.color.bg.emphasis["primary-subtle"]};
        top: 0;
        bottom: 0;
        left: -10px;
        width: 10px;
        z-index: 1;
      }
    }

    &.semi-selected + .selected {
      position: relative;

      &::before {
        position: absolute;
        content: "";
        background: ${theme.color.bg.emphasis["primary-subtle"]};
        top: 0;
        bottom: 0;
        left: 0;
        width: 8px;
        z-index: 1;
      }
    }

    &.selected + .selected {
      position: relative;

      &::before {
        position: absolute;
        content: "";
        background: ${theme.color.bg.emphasis["primary-subtle"]};
        top: 0;
        bottom: 0;
        left: -8px;
        width: 16px;
        z-index: 1;
      }
    }

    &.semi-selected {
      background: ${theme.color.bg.emphasis["primary-subtle"]};
    }

    &:hover ${StyledDay} {
      background-color: ${theme.color.bg.emphasis.primary};
      color: ${theme.color.fg.onemphasis.default};
    }

    ${isStartDay &&
    css`
      & {
        border-top-left-radius: ${theme.radius.rounded};
        border-bottom-left-radius: ${theme.radius.rounded};
      }
    `}

    ${isEndDay &&
    css`
      & {
        border-top-right-radius: ${theme.radius.rounded};
        border-bottom-right-radius: ${theme.radius.rounded};
      }
    `}
  `
);

export const StyledDay = styled.span(
  ({ theme }) => css`
    z-index: 2;
    position: sticky;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${theme.radius.rounded};
    width: 32px;
    height: 28px;
    line-height: 28px;
  `
);
